"use client";

/**
 * Google Ads Conversion Tracker
 *
 * Fires Google Ads conversion events on the client side by detecting
 * server-set cookies and URL parameters. This component should be included
 * in the root layout alongside GoogleTagManager and AnalyticsTracker.
 *
 * Conversions tracked:
 * 1. Account Creation — via `vs_new_signup` cookie set during OAuth signup
 * 2. Channel Connected — via `?connected=platform` URL param on /settings/accounts
 * 3. Purchase — via `?purchase=success` or `?subscription=success` on /seeds
 */

import { useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import {
  trackSignUpConversion,
  trackChannelConnectedConversion,
  trackPurchaseConversion,
} from "@/lib/analytics/gtag";

/** Delete a cookie by name (client-side). */
function deleteCookie(name: string): void {
  document.cookie = `${name}=; path=/; max-age=0; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
}

/** Read a cookie by name (client-side). */
function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}=([^;]*)`)
  );
  return match ? decodeURIComponent(match[1]) : null;
}

export function ConversionTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const firedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!pathname) return;

    // Avoid double-firing on React strict mode re-renders
    const fireOnce = (key: string, fn: () => void) => {
      if (firedRef.current.has(key)) return;
      firedRef.current.add(key);
      fn();
    };

    // 1. Account Creation — check for the signup cookie
    const signupCookie = getCookie("vs_new_signup");
    if (signupCookie === "1") {
      fireOnce("signup", () => {
        trackSignUpConversion();
        deleteCookie("vs_new_signup");
      });
    }

    // 2. Channel Connected — check for ?connected=platform
    const connectedPlatform = searchParams.get("connected");
    if (connectedPlatform) {
      fireOnce(`channel_${connectedPlatform}`, () => {
        trackChannelConnectedConversion(connectedPlatform);
      });
    }

    // 3. Purchase — check for ?purchase=success or ?subscription=success
    const purchase = searchParams.get("purchase");
    const subscription = searchParams.get("subscription");
    if (purchase === "success") {
      fireOnce("purchase", () => {
        trackPurchaseConversion();
      });
    }
    if (subscription === "success") {
      fireOnce("subscription", () => {
        trackPurchaseConversion();
      });
    }
  }, [pathname, searchParams]);

  return null;
}
